/**
 * Validation for a laboratory's own sample types and their additional intake
 * fields (0024, decisions D296-D305).
 *
 * READ CLAUDE.md SECTION 4. A sample type says what a physical sample IS and
 * what a laboratory wants to know about it when it ARRIVES. An additional
 * field is free text, a number, a date, a time, a choice or a yes/no that a
 * person states at the counter: there is no formula, condition, computed value,
 * limit or pass/fail anywhere in this file, and none may be added.
 *
 * Which values a sample may hold is decided against the laboratory's own
 * definitions by the backend; `checkAdditionalFieldValue` is the one shape
 * check both sides share.
 */
import { z } from 'zod';
import { ALL_SAMPLE_FIELD_TYPES, OPTION_VALUE_PATTERN, STABLE_KEY_PATTERN, } from '@lims/shared-types';
import { isoDateSchema, isoTimestampSchema, uuidSchema } from './primitives.js';
/** An optional label or text: absent, or actual content. Never blank. */
const optionalLabel = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
/** A sample type's code, as samples carry it (`samples.sample_type`). */
export const sampleTypeCodeSchema = z
    .string()
    .regex(STABLE_KEY_PATTERN, 'A code is lower-case letters, digits and _, starting with a letter.');
export const sampleFieldTypeSchema = z.enum(ALL_SAMPLE_FIELD_TYPES);
/** At least one of the two names: the interface falls back to the other. */
function oneOf(ar, en) {
    return Boolean(ar) || Boolean(en);
}
export const MAX_FIELDS_PER_SAMPLE_TYPE = 50;
export const MAX_OPTIONS_PER_FIELD = 100;
/**
 * One option of a Select field, in the complete ordered list a field save
 * sends. An `id` names an existing option (its value is fixed); no `id` adds
 * one, with `value` generated from its label when absent. An existing option
 * missing from the list is deleted when never chosen, refused otherwise.
 */
export const sampleTypeFieldOptionInputSchema = z
    .object({
    id: uuidSchema.optional(),
    value: z.string().regex(OPTION_VALUE_PATTERN).optional(),
    labelAr: optionalLabel(120),
    labelEn: optionalLabel(120),
    archived: z.boolean().optional(),
})
    .refine((option) => oneOf(option.labelAr, option.labelEn), {
    message: 'An option needs an Arabic or an English label.',
    path: ['labelEn'],
});
const fieldPresentation = {
    labelAr: optionalLabel(120),
    labelEn: optionalLabel(120),
    placeholderAr: optionalLabel(200),
    placeholderEn: optionalLabel(200),
    helpAr: optionalLabel(500),
    helpEn: optionalLabel(500),
};
const optionsListSchema = z.array(sampleTypeFieldOptionInputSchema).max(MAX_OPTIONS_PER_FIELD);
/** Add an additional field to a sample type. */
export const createSampleTypeFieldSchema = z
    .object({
    /** Generated from the English label when absent; fixed once created. */
    key: z.string().regex(STABLE_KEY_PATTERN).optional(),
    fieldType: sampleFieldTypeSchema,
    ...fieldPresentation,
    isRequired: z.boolean().default(false),
    options: optionsListSchema.optional(),
})
    .refine((field) => oneOf(field.labelAr, field.labelEn), {
    message: 'A field needs an Arabic or an English label.',
    path: ['labelEn'],
})
    .refine((field) => field.fieldType !== 'select' || (field.options ?? []).length > 0, {
    message: 'A Select field needs at least one option.',
    path: ['options'],
});
/**
 * Change a field. Labels, help, placeholders and Optional/Required always may
 * change; the data type only while no sample holds a value for it (D299).
 * `options`, when sent, is the complete ordered list.
 */
export const updateSampleTypeFieldSchema = z
    .object({
    fieldType: sampleFieldTypeSchema.optional(),
    ...fieldPresentation,
    isRequired: z.boolean().optional(),
    options: optionsListSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
});
/** Create a sample type, with any additional fields it starts with. */
export const createSampleTypeSchema = z
    .object({
    /** Generated from the English name when absent; fixed once created. */
    code: sampleTypeCodeSchema.optional(),
    nameAr: optionalLabel(120),
    nameEn: optionalLabel(120),
    fields: z.array(createSampleTypeFieldSchema).max(MAX_FIELDS_PER_SAMPLE_TYPE).optional(),
})
    .refine((type) => oneOf(type.nameAr, type.nameEn), {
    message: 'A sample type needs an Arabic or an English name.',
    path: ['nameEn'],
});
export const updateSampleTypeSchema = z
    .object({
    nameAr: optionalLabel(120),
    nameEn: optionalLabel(120),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
});
/** A complete new order: every field (or type) id, once. */
export const reorderSchema = z.object({
    ids: z
        .array(uuidSchema)
        .min(1)
        .max(200)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'Each id is listed once.' }),
});
// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------
/** One stated value as it travels: text, a number, or yes/no. */
const additionalValueSchema = z.union([z.string().max(2000), z.number().finite(), z.boolean()]);
/**
 * A sample's additional details, keyed by FIELD ID (D298). Which ids are
 * allowed, and which are required, is the backend's decision against the
 * sample's own type in this laboratory.
 */
export const additionalFieldsSchema = z
    .record(uuidSchema, additionalValueSchema)
    .refine((value) => Object.keys(value).length <= MAX_FIELDS_PER_SAMPLE_TYPE, {
    message: `At most ${MAX_FIELDS_PER_SAMPLE_TYPE} additional details.`,
});
/** The same on Edit Sample, where null clears a value. */
export const additionalFieldsPatchSchema = z
    .record(uuidSchema, additionalValueSchema.nullable())
    .refine((value) => Object.keys(value).length <= MAX_FIELDS_PER_SAMPLE_TYPE * 2, {
    message: 'Too many additional details.',
});
const TIME_SHAPE = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
 * Check one stated value against its field's data type (D298). An empty
 * string is NO answer, never a value of "" or 0 (CLAUDE.md section 8). Whether
 * an option id belongs to the field is the backend's check.
 */
export function checkAdditionalFieldValue(fieldType, raw) {
    if (raw === null || raw === undefined)
        return { ok: true, empty: true };
    if (typeof raw === 'string' && raw.trim() === '')
        return { ok: true, empty: true };
    const bad = { ok: false, code: 'invalid_value' };
    switch (fieldType) {
        case 'text':
        case 'long_text': {
            if (typeof raw !== 'string')
                return bad;
            const text = raw.trim();
            if (text.length > (fieldType === 'text' ? 500 : 2000))
                return bad;
            return { ok: true, empty: false, column: 'text', value: text };
        }
        case 'number': {
            const number = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.trim()) : NaN;
            if (typeof raw === 'boolean' || !Number.isFinite(number))
                return bad;
            return { ok: true, empty: false, column: 'number', value: number };
        }
        case 'date':
            return typeof raw === 'string' && isoDateSchema.safeParse(raw.trim()).success
                ? { ok: true, empty: false, column: 'date', value: raw.trim() }
                : bad;
        case 'time':
            return typeof raw === 'string' && TIME_SHAPE.test(raw.trim())
                ? { ok: true, empty: false, column: 'time', value: raw.trim() }
                : bad;
        case 'datetime':
            return typeof raw === 'string' && isoTimestampSchema.safeParse(raw.trim()).success
                ? { ok: true, empty: false, column: 'datetime', value: raw.trim() }
                : bad;
        case 'boolean':
            return typeof raw === 'boolean' ? { ok: true, empty: false, column: 'boolean', value: raw } : bad;
        case 'select':
            return typeof raw === 'string' && uuidSchema.safeParse(raw).success
                ? { ok: true, empty: false, column: 'option', value: raw }
                : bad;
        default:
            return bad;
    }
}
//# sourceMappingURL=sample-types.js.map